import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { plainToClass } from 'class-transformer';

import { environment } from '../../../environments/environment';
import { ProgressIndicatorService } from './progress-indicator.service';
import { CollectApiServiceBase } from './collect-api-base.service';
import { MagicStructureDefinition } from '../models/magic-structures/magic-structure-def.model';
import { MagicFolderDefinition } from '../models/magic-structures/magic-folder-def.model';
import { MagicFormInstanceDefinition } from '../models/magic-structures/magic-form-instance-def.model';
import { MagicStructureMembership } from '../models/magic-structures/magic-structure-membership.model';
import { Folder } from '../models/site-content/folder.model';
import { FormInstance } from '../models/site-content/form-instance.model';

@Injectable()
export class MagicStructureService extends CollectApiServiceBase<MagicStructureDefinition> {
    // Constructor.
    public constructor(http: HttpClient, progressIndicatorService: ProgressIndicatorService) {
        super(http, progressIndicatorService, environment.apiUrl, 'magicStructure', MagicStructureDefinition)
    }

    // Overrides.
    // None so far.

    // Service methods.
    public createMagicFolder(magicFolderDef: MagicFolderDefinition): Promise<MagicFolderDefinition> {
        let url = `${this.url}/${this.endpoint}/createMagicFolder`;

        this.updateProgress(50, 75, `Saving magic folder ...`); 

        return this.http.post<MagicFolderDefinition>(url, magicFolderDef)
            .toPromise()
            .then(response => {
                let obj: MagicFolderDefinition = plainToClass(MagicFolderDefinition, response);

                this.updateProgress(100, 100, `Saved the magic folder`);

                return obj;
            })
            .catch(this.handleError);
    }

    public createMagicFormInstance(magicFormInstanceDef: MagicFormInstanceDefinition): Promise<MagicFormInstanceDefinition> {
        let url = `${this.url}/${this.endpoint}/createMagicFormInstance`;

        this.updateProgress(50, 75, `Saving form template ...`);

        return this.http.post<MagicFormInstanceDefinition>(url, magicFormInstanceDef)
            .toPromise()
            .then(response => {
                let obj: MagicFormInstanceDefinition = plainToClass(MagicFormInstanceDefinition, response);

                this.updateProgress(100, 100, `Saved the magic form`);

                return obj;
            })
            .catch(this.handleError);
    }

    public getMembershipImportFormatExample(magicStructureDef: MagicStructureDefinition): Promise<string> {
        let url = `${this.url}/${this.endpoint}/getMembershipImportFormatExample`;

        return this.http.post<string>(url, magicStructureDef)
            .toPromise()
            .then(response => {
                return response;
            })
            .catch(this.handleError);
    }

    public deleteMagicFolder(magicFolderId: number): Promise<boolean> {
        let url = `${this.url}/${this.endpoint}/deleteMagicFolder/${magicFolderId}`;

        return this.http.delete<boolean>(url)
            .toPromise()
            .then(response => {
                return response;
            })
            .catch(this.handleError);
    }

    public deleteMagicFormInstance(magicFormInstanceId: number): Promise<boolean> {
        let url = `${this.url}/${this.endpoint}/deleteMagicFormInstance/${magicFormInstanceId}`;

        return this.http.delete<boolean>(url)
            .toPromise()
            .then(response => {
                return response;
            })
            .catch(this.handleError);
    }

    public getMagicFolders(dataCollectionId: number): Promise<Folder[]> {
        let url = `${this.url}/${this.endpoint}/getMagicFolders/${dataCollectionId}`;

        //this.updateProgress(50, 75, `Getting magic folders ...`);

        return this.http.get<Folder[]>(url)
            .toPromise()
            .then(response => {
                let folders: Folder[] = [];

                if (response != null) {
                    for (let iFolder: number = 0; iFolder < response.length; iFolder++) {
                        let oFolder = response[iFolder];
                        let folder: Folder = plainToClass(Folder, oFolder);

                        folders.push(folder);
                    }
                }

                //this.updateProgress(100, 100, `Completed`);

                return (folders);
            })
            .catch(this.handleError);
    }

    public getMagicFormInstances(dataCollectionId: number): Promise<FormInstance[]> {
        let url = `${this.url}/${this.endpoint}/getMagicFormInstances/${dataCollectionId}`;

        //this.updateProgress(50, 75, `Getting magic forms ...`);

        return this.http.get<FormInstance[]>(url)
            .toPromise()
            .then(response => {
                let formInstances: FormInstance[] = [];

                if (response != null) {
                    for (let iFormInstance: number = 0; iFormInstance < response.length; iFormInstance++) {
                        let oFormInstance = response[iFormInstance];
                        let formInstance: FormInstance = plainToClass(FormInstance, oFormInstance);

                        formInstances.push(formInstance);
                    }
                }

                //this.updateProgress(100, 100, `Completed`);

                return (formInstances);
            })
            .catch(this.handleError);
    }

    public getUserMagicFolders(magicFolderId: number): Promise<Folder[]> {
        let url = `${this.url}/${this.endpoint}/getUserMagicFolders/${magicFolderId}`;

        return this.http.get<Folder[]>(url)
            .toPromise()
            .then(response => {
                let folders: Folder[] = [];

                if (response != null) {
                    for (let iFolder: number = 0; iFolder < response.length; iFolder++) {
                        let oFolder = response[iFolder];
                        let folder: Folder = plainToClass(Folder, oFolder);

                        folders.push(folder);
                    }
                }

                return (folders);
            })
            .catch(this.handleError);
    }

    public getUserMagicFormInstances(magicFormInstanceId: number): Promise<FormInstance[]> {
        let url = `${this.url}/${this.endpoint}/getUserMagicFormInstances/${magicFormInstanceId}`;

        return this.http.get<FormInstance[]>(url)
            .toPromise()
            .then(response => {
                let formInstances: FormInstance[] = [];

                if (response != null) {
                    for (let iFormInstance: number = 0; iFormInstance < response.length; iFormInstance++) {
                        let oFormInstance = response[iFormInstance];
                        let formInstance: FormInstance = plainToClass(FormInstance, oFormInstance);

                        formInstances.push(formInstance);
                    }
                }

                return (formInstances);
            })
            .catch(this.handleError);
    }

    public getMagicStructureMemberships(magicStructureDefinitionId: number): Promise<MagicStructureMembership[]> {
        let url = `${this.url}/${this.endpoint}/getMagicStructureMemberships/${magicStructureDefinitionId}`;

        return this.http.get<FormInstance[]>(url)
            .toPromise()
            .then(response => {
                let memberships: MagicStructureMembership[] = [];

                if (response != null) {
                    for (let iMembership: number = 0; iMembership < response.length; iMembership++) {
                        let oMembership = response[iMembership];
                        let membership: MagicStructureMembership = plainToClass(MagicStructureMembership, oMembership);

                        memberships.push(membership);
                    }
                }

                return (memberships);
            })
            .catch(this.handleError);
    }
}
